export type ContractRenamePreviewRow = {
  global_event_id: number;
  event_code: number;
  event_type: string;
  event_contract_name_old: string;
  event_contract_name_new: string;
  version: number | null;
  side: "rising" | "resolving";
  status: "ready" | "conflict" | "unchanged" | "skipped";
  reason?: string | null;
};

export type ContractRenamePreviewResponse = {
  anchor_global_event_id: number;
  rising_contract_name_current?: string | null;
  resolving_contract_name_current?: string | null;
  new_rising_event_contract_name?: string | null;
  new_resolving_event_contract_name?: string | null;
  /** Legacy single-name field when only one side renamed */
  event_contract_name_old: string;
  new_event_contract_name?: string | null;
  preview_token: string;
  total: number;
  ready_count: number;
  conflict_count: number;
  unchanged_count: number;
  skipped_count?: number;
  rows: ContractRenamePreviewRow[];
};

export type ContractRenameApplyResponse = {
  detail: string;
  applied_count: number;
  applied_global_event_ids: number[];
  preview: ContractRenamePreviewResponse;
};

export type ContractRenamePreviewPayload = {
  global_event_id?: number;
  event_code?: number;
  event_type?: string;
  event_contract_name?: string;
  /** Rename rising side only (optional) */
  new_rising_event_contract_name?: string;
  /** Rename resolving side only (optional) */
  new_resolving_event_contract_name?: string;
  /** Legacy: applies to both sides when side-specific fields omitted */
  new_event_contract_name?: string;
  discover_only?: boolean;
};

const EVENT_CONTRACT_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

/** Validate when user entered a value; empty is allowed (side unchanged). */
export function validateOptionalEventContractName(value: unknown): string | null {
  const raw = String(value ?? "");
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (/\s/.test(raw)) {
    return "Must not contain spaces";
  }
  if (!EVENT_CONTRACT_NAME_PATTERN.test(trimmed)) {
    return "Use English letters only (PascalCase), e.g. ChargingContactorOpened";
  }
  return null;
}

export function validateEventContractName(value: unknown): string | null {
  const err = validateOptionalEventContractName(value);
  if (err) {
    return err;
  }
  if (!String(value ?? "").trim()) {
    return "Required";
  }
  return null;
}

export const canApplyContractRename = (
  loading: boolean,
  conflictCount: number,
  readyCount: number,
  confirmText: string
): boolean =>
  !loading && conflictCount <= 0 && readyCount > 0 && confirmText.trim().toUpperCase() === "APPLY";
